import { useState, useEffect, useRef } from "react";
import axios from "axios";
import Footer from '../components/Footer';
import { FaImage, FaExternalLinkAlt, FaTrophy, FaArrowRight, FaExclamationTriangle, FaTimes, FaUser, FaBuilding, FaEnvelope, FaPhone, FaHandPointer, FaRobot } from "react-icons/fa";
import ReCAPTCHA from "react-google-recaptcha";

function BharatStartupGrandChallengePage() {
    const API_URL = `${process.env.REACT_APP_API_BASE_URL}/UIcmsPage`;
    const API_MEDIA = `${process.env.REACT_APP_API_BASE_URL}/UImedia`;
    const API_WEB_ENQUIRY = `${process.env.REACT_APP_API_BASE_URL}/web-enquiries`;

    // State for page data
    const [pageData, setPageData] = useState(null);
    const [bannerImage, setBannerImage] = useState(null);
    const [challengeImages, setChallengeImages] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [imageLoadErrors, setImageLoadErrors] = useState(new Set());
    
    // State for Web Enquiry Popup
    const [showEnquiryPopup, setShowEnquiryPopup] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    
    // Form state
    const [formData, setFormData] = useState({
        fullName: "",
        organisation: "",
        email: "",
        mobile: "",
        associationType: "",
        interests: [],
        contribution: "",
        confirmInterest: false,
        consentContact: false
    });

    // reCAPTCHA state
    const [recaptchaToken, setRecaptchaToken] = useState(null);
    const [recaptchaError, setRecaptchaError] = useState(false);
    const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
    const [recaptchaScriptError, setRecaptchaScriptError] = useState(false);

    // Refs
    const loadedImagesRef = useRef(new Set());
    const imageCacheRef = useRef({});
    const popupRef = useRef(null);
    const enquiryButtonRef = useRef(null);
    const recaptchaRef = useRef(null);

    // reCAPTCHA site key
    const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

    // Load reCAPTCHA script dynamically
    useEffect(() => {
        const loadRecaptchaScript = () => {
            // Check if already loaded
            if (window.grecaptcha) {
                setRecaptchaLoaded(true);
                return;
            }

            // Check if script already exists
            const existingScript = document.getElementById('google-recaptcha-script');
            if (existingScript) {
                setRecaptchaLoaded(true);
                return;
            }

            // Create and load script
            const script = document.createElement('script');
            script.id = 'google-recaptcha-script';
            script.src = `https://www.google.com/recaptcha/api.js?render=explicit`;
            script.async = true;
            script.defer = true;
            script.onload = () => {
                setRecaptchaLoaded(true);
                setRecaptchaScriptError(false);
            };
            script.onerror = () => {
                setRecaptchaScriptError(true);
                setRecaptchaLoaded(false);
                console.error('reCAPTCHA script failed to load');
            };
            
            document.head.appendChild(script);
        };

        if (showEnquiryPopup && RECAPTCHA_SITE_KEY) {
            loadRecaptchaScript();
        }
    }, [showEnquiryPopup, RECAPTCHA_SITE_KEY]);

    // Close popup when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popupRef.current && !popupRef.current.contains(event.target) && 
                !event.target.closest('.web-enquiry-button')) {
                setShowEnquiryPopup(false);
            }
        };

        if (showEnquiryPopup) {
            document.addEventListener('mousedown', handleClickOutside);
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.body.style.overflow = 'auto';
        };
    }, [showEnquiryPopup]);

    // Reset reCAPTCHA when popup closes
    useEffect(() => {
        if (!showEnquiryPopup && recaptchaRef.current) {
            recaptchaRef.current.reset();
            setRecaptchaToken(null);
            setRecaptchaError(false);
        }
    }, [showEnquiryPopup]);

    // Fetch CMS Page data
    useEffect(() => {
        const fetchPageData = async () => {
            try {
                setLoading(true);
                const response = await axios.get(API_URL);

                if (response.data.success && response.data.data && response.data.data.length > 0) {
                    const activePage = response.data.data.find(page => page.isActive === true);

                    if (activePage) {
                        setPageData(activePage);

                        if (activePage.banner?.image?.id) {
                            await fetchBannerImage(activePage.banner.image.id);
                        }

                        if (activePage.challenges?.items && activePage.challenges.items.length > 0) {
                            await fetchAllChallengeImages(activePage.challenges.items);
                        }
                    } else {
                        setError("No active CMS page found");
                    }
                } else {
                    setError("No CMS page data available");
                }
            } catch (err) {
                setError("Failed to load page data");
            } finally {
                setLoading(false);
            }
        };

        fetchPageData();
    }, [API_URL]);

    // Fetch banner image with retry
    const fetchBannerImage = async (imageId) => {
        if (imageCacheRef.current[`banner_${imageId}`]) {
            setBannerImage(imageCacheRef.current[`banner_${imageId}`]);
            return;
        }

        try {
            const imageUrl = `${API_MEDIA}/${imageId}/download`;

            const response = await axios.get(imageUrl, {
                responseType: 'blob',
                timeout: 15000,
                headers: {
                    'Cache-Control': 'no-cache'
                }
            });

            if (response.data && response.data.size > 0) {
                const blobUrl = URL.createObjectURL(response.data);
                imageCacheRef.current[`banner_${imageId}`] = blobUrl;
                setBannerImage(blobUrl);
                loadedImagesRef.current.add(`banner_${imageId}`);
            } else {
                setImageLoadErrors(prev => new Set(prev).add(`banner_${imageId}`));
            }
        } catch (err) {
            setImageLoadErrors(prev => new Set(prev).add(`banner_${imageId}`));
            try {
                const directUrl = `${API_MEDIA}/${imageId}/view`;
                const img = new Image();
                img.onload = () => {
                    setBannerImage(directUrl);
                    imageCacheRef.current[`banner_${imageId}`] = directUrl;
                };
                img.src = directUrl;
            } catch (fallbackErr) {
                // Silently fail
            }
        }
    };

    // Fetch all challenge images
    const fetchAllChallengeImages = async (challenges) => {
        const images = {};
        const failedImages = new Set();

        const batchSize = 3;
        for (let i = 0; i < challenges.length; i += batchSize) {
            const batch = challenges.slice(i, i + batchSize);
            await Promise.all(
                batch.map(async (challenge, batchIndex) => {
                    const index = i + batchIndex;

                    if (challenge.image?.id) {
                        const imageId = challenge.image.id;
                        const cacheKey = `challenge_${challenge.number || index}_${imageId}`;

                        if (imageCacheRef.current[cacheKey]) {
                            images[challenge.number || index] = imageCacheRef.current[cacheKey];
                            return;
                        }

                        try {
                            const imageUrl = `${API_MEDIA}/${imageId}/download`;

                            const response = await axios.get(imageUrl, {
                                responseType: 'blob',
                                timeout: 10000,
                                headers: {
                                    'Cache-Control': 'no-cache'
                                }
                            });

                            if (response.data && response.data.size > 0) {
                                const blobUrl = URL.createObjectURL(response.data);
                                imageCacheRef.current[cacheKey] = blobUrl;
                                images[challenge.number || index] = blobUrl;
                                loadedImagesRef.current.add(cacheKey);
                            } else {
                                failedImages.add(cacheKey);
                            }
                        } catch (err) {
                            failedImages.add(cacheKey);
                            try {
                                const directUrl = `${API_MEDIA}/${imageId}/view`;
                                const img = new Image();
                                img.onload = () => {
                                    images[challenge.number || index] = directUrl;
                                    imageCacheRef.current[cacheKey] = directUrl;
                                    setChallengeImages(prev => ({ ...prev, [challenge.number || index]: directUrl }));
                                };
                                img.src = directUrl;
                            } catch (fallbackErr) {
                                // Silently fail
                            }
                        }
                    }
                })
            );
        }

        setChallengeImages(prev => ({ ...prev, ...images }));

        if (failedImages.size > 0) {
            setImageLoadErrors(prev => {
                const newSet = new Set(prev);
                failedImages.forEach(key => newSet.add(key));
                return newSet;
            });
        }
    };

    // Function to retry loading a specific image
    const retryImageLoad = async (imageId, isBanner = false, challengeNumber = null) => {
        if (isBanner) {
            await fetchBannerImage(imageId);
        } else if (challengeNumber !== null) {
            const challenge = pageData?.challenges?.items?.find(c =>
                c.number === challengeNumber.toString() ||
                c.image?.id === imageId
            );
            if (challenge) {
                const newImages = { ...challengeImages };
                const cacheKey = `challenge_${challengeNumber}_${imageId}`;

                try {
                    const response = await axios.get(`${API_MEDIA}/${imageId}/download`, {
                        responseType: 'blob',
                        timeout: 10000
                    });

                    if (response.data && response.data.size > 0) {
                        const blobUrl = URL.createObjectURL(response.data);
                        imageCacheRef.current[cacheKey] = blobUrl;
                        newImages[challengeNumber] = blobUrl;
                        setChallengeImages(newImages);

                        setImageLoadErrors(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(cacheKey);
                            return newSet;
                        });
                    }
                } catch (err) {
                    // Silently fail
                }
            }
        }
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (type === 'checkbox' && name === 'associationType') {
            setFormData(prev => ({
                ...prev,
                associationType: value
            }));
        } else if (type === 'checkbox' && (name === 'confirmInterest' || name === 'consentContact')) {
            setFormData(prev => ({
                ...prev,
                [name]: checked
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    // Handle interest checkboxes
    const handleInterestChange = (interest) => {
        setFormData(prev => {
            const interests = [...prev.interests];
            if (interests.includes(interest)) {
                return {
                    ...prev,
                    interests: interests.filter(i => i !== interest)
                };
            } else {
                return {
                    ...prev,
                    interests: [...interests, interest]
                };
            }
        });
    };

    // Handle reCAPTCHA change
    const handleRecaptchaChange = (token) => {
        setRecaptchaToken(token);
        setRecaptchaError(false);
    };

    // Handle reCAPTCHA expire
    const handleRecaptchaExpire = () => {
        setRecaptchaToken(null);
        setRecaptchaError(true);
    };

    // Handle form submission - UPDATED WITH BACKEND API CALL
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus(null);
        setRecaptchaError(false);

        // Basic validation
        if (!formData.fullName || !formData.email || !formData.mobile || !formData.associationType) {
            setSubmitStatus({
                type: 'error',
                message: 'Please fill all required fields'
            });
            setIsSubmitting(false);
            return;
        }

        if (!formData.confirmInterest) {
            setSubmitStatus({
                type: 'error',
                message: 'Please confirm your interest'
            });
            setIsSubmitting(false);
            return;
        }

        // reCAPTCHA validation
        if (!recaptchaToken) {
            setRecaptchaError(true);
            setSubmitStatus({
                type: 'error',
                message: 'Please complete the reCAPTCHA verification'
            });
            setIsSubmitting(false);
            return;
        }

        try {
            // Prepare form data with reCAPTCHA token
            const submissionData = {
                fullName: formData.fullName,
                organisation: formData.organisation,
                email: formData.email,
                mobile: formData.mobile,
                associationType: formData.associationType,
                interests: formData.interests,
                contribution: formData.contribution,
                confirmInterest: formData.confirmInterest,
                consentContact: formData.consentContact,
                source: 'Bharat Startup Grand Challenge Page',
                recaptchaToken: recaptchaToken
            };
            
            // Make API call to backend
            const response = await axios.post(API_WEB_ENQUIRY, submissionData, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.data.success) {
                setSubmitStatus({
                    type: 'success',
                    message: response.data.message || 'Thank you! Your enquiry has been submitted successfully. We will contact you soon.'
                });
                
                // Reset form after successful submission
                setTimeout(() => {
                    setFormData({
                        fullName: "",
                        organisation: "",
                        email: "",
                        mobile: "",
                        associationType: "",
                        interests: [],
                        contribution: "",
                        confirmInterest: false,
                        consentContact: false
                    });
                    
                    // Reset reCAPTCHA
                    if (recaptchaRef.current) {
                        recaptchaRef.current.reset();
                    }
                    setRecaptchaToken(null);
                    
                    setIsSubmitting(false);
                    setTimeout(() => setShowEnquiryPopup(false), 2000);
                }, 2000);
                
            } else {
                throw new Error(response.data.message || 'Submission failed');
            }
            
        } catch (error) {
            console.error('Submission error:', error);
            
            let errorMessage = 'Something went wrong. Please try again.';
            
            if (error.response) {
                // Backend se error aaya
                if (error.response.data && error.response.data.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response.status === 400) {
                    errorMessage = 'Validation error. Please check your inputs.';
                } else if (error.response.status === 500) {
                    errorMessage = 'Server error. Please try again later.';
                }
            } else if (error.request) {
                // Request bheji but response nahi aaya
                errorMessage = 'Network error. Please check your connection.';
            }
            
            setSubmitStatus({
                type: 'error',
                message: errorMessage
            });
            
            // Reset reCAPTCHA on error
            if (recaptchaRef.current) {
                recaptchaRef.current.reset();
            }
            setRecaptchaToken(null);
            setIsSubmitting(false);
        }
    };

    // Clean up image URLs on unmount
    useEffect(() => {
        return () => {
            Object.values(imageCacheRef.current).forEach(url => {
                if (typeof url === 'string' && url.startsWith('blob:')) {
                    URL.revokeObjectURL(url);
                }
            });
        };
    }, []);

    // Loading state
    if (loading) {
        return (
            <div className="w-full bg-gray-800 text-white min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-yellow-500 mx-auto mb-4"></div>
                    <p className="text-xl text-gray-300">Loading Bharat Startup Grand Challenge...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error || !pageData) {
        return (
            <div className="w-full bg-gray-800 text-white min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <FaExclamationTriangle className="w-10 h-10 text-red-400" />
                    </div>
                    <h2 className="text-2xl font-bold text-red-400 mb-2">Unable to Load Page</h2>
                    <p className="text-gray-400 mb-4">{error || "No data available"}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-6 py-2 bg-yellow-500 text-gray-900 font-semibold rounded-lg hover:bg-yellow-600 transition-colors"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    const hasChallenges = pageData.challenges?.items && Array.isArray(pageData.challenges.items) && pageData.challenges.items.length > 0;

    return (
        <div className="w-full bg-gray-800 text-white min-h-screen">
            {/* BANNER SECTION */}
            <div className="relative w-full h-[70vh] md:h-screen overflow-hidden">
                {bannerImage ? (
                    <img
                        src={bannerImage}
                        alt={pageData.banner?.title || "Bharat Startup Grand Challenge"}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                            e.target.style.display = "none";
                            if (pageData.banner?.image?.id) {
                                retryImageLoad(pageData.banner.image.id, true);
                            }
                        }}
                    />
                ) : (
                    <div className="w-full h-full bg-gradient-to-br from-gray-900 to-yellow-900/30 flex items-center justify-center">
                        <div className="text-center">
                            <FaImage className="text-6xl text-gray-600 mx-auto mb-4" />
                            <p className="text-xl text-gray-400">Banner Image Loading...</p>
                        </div>
                    </div>
                )}

                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent flex items-end">
                    <div className="w-full max-w-6xl mx-auto px-6 pb-12">
                        <h1 className="text-4xl md:text-5xl font-bold text-white/60 mb-4">
                            {pageData.banner?.title || "Bharat Startup Grand Challenge"}
                        </h1>
                        <p className="text-xl text-gray-300">
                            {pageData.banner?.subtitle || "DPIIT-Approved National Startup Innovation Initiative"}
                        </p>
                    </div>
                </div>
            </div>

            {/* WEB ENQUIRY BUTTON */}
            <div className="relative -mt-8 z-40">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="flex justify-center">
                        <button
                            ref={enquiryButtonRef}
                            onClick={() => setShowEnquiryPopup(true)}
                            className="web-enquiry-button group animate-bounce hover:animate-none transform hover:-translate-y-1 transition-all duration-300"
                        >
                            <div className="relative">
                                <div className="absolute -inset-3 bg-yellow-500/30 rounded-full animate-ping opacity-75"></div>
                                <div className="absolute -inset-2 bg-yellow-500/20 rounded-full"></div>
                                
                                <div className="relative px-8 py-4 bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-500 text-gray-900 font-bold text-xl rounded-2xl shadow-2xl hover:shadow-yellow-500/50 transition-all duration-300 group-hover:scale-105 group-hover:from-yellow-400 group-hover:via-yellow-500 group-hover:to-yellow-400 flex items-center gap-3 border-2 border-yellow-700">
                                    <FaHandPointer className="text-2xl animate-pulse group-hover:animate-none" />
                                    <span className="text-lg md:text-xl">Join the Bharat Grand Startup Challenge (BGSC)</span>
                                    <div className="w-10 h-10 rounded-full bg-white/30 flex items-center justify-center group-hover:bg-white/40 transition-colors">
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                </div>
                                
                                <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
                                    <div className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-yellow-500"></div>
                                </div>
                            </div>
                        </button>
                    </div>
                    
                    <div className="text-center mt-6 animate-fade-in">
                        <p className="text-gray-300 text-lg font-medium">
                            Join India's Startup Revolution! 
                            <span className="text-yellow-400 ml-2">Click to Register Now</span>
                        </p>
                        <p className="text-gray-400 text-sm mt-1">
                            Be part of the DPIIT-approved national startup innovation initiative
                        </p>
                    </div>
                </div>
            </div>

            {/* Web Enquiry Popup Modal with reCAPTCHA */}
            {showEnquiryPopup && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
                    <div 
                        ref={popupRef}
                        className="bg-gray-900 rounded-2xl border-3 border-yellow-500 w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-scale-in"
                    >
                        {/* Popup Header */}
                        <div className="sticky top-0 bg-gray-900 border-b border-gray-800 p-6 flex items-center justify-between">
                            <div>
                                <h2 className="text-2xl font-bold text-yellow-400">Join the Bharat Grand Startup Challenge (BGSC)</h2>
                                <p className="text-gray-400 mt-1">Choose your role. Be a part of Bharat's startup growth journey.</p>
                            </div>
                            <button
                                onClick={() => setShowEnquiryPopup(false)}
                                className="w-10 h-10 rounded-full bg-gray-800 hover:bg-red-500/20 hover:text-red-400 flex items-center justify-center transition-colors"
                            >
                                <FaTimes />
                            </button>
                        </div>

                        {/* Popup Content - Form */}
                        <form onSubmit={handleSubmit} className="p-6 space-y-6">
                            {/* Section A: Personal Information */}
                            <div className="space-y-4">
                                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                    <FaUser className="text-yellow-500" />
                                    Personal information
                                </h3>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Full Name <span className="text-red-400">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="fullName"
                                            value={formData.fullName}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500"
                                            placeholder="Enter your full name"
                                            required
                                        />
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Organisation
                                        </label>
                                        <div className="relative">
                                            <FaBuilding className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                                            <input
                                                type="text"
                                                name="organisation"
                                                value={formData.organisation}
                                                onChange={handleInputChange}
                                                className="w-full px-10 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500"
                                                placeholder="Your organisation"
                                            />
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Email <span className="text-red-400">*</span>
                                        </label>
                                        <div className="relative">
                                            <FaEnvelope className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                                            <input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                className="w-full px-10 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500"
                                                placeholder="your@email.com"
                                                required
                                            />
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Mobile <span className="text-red-400">*</span>
                                        </label>
                                        <div className="relative">
                                            <FaPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                                            <input
                                                type="tel"
                                                name="mobile"
                                                value={formData.mobile}
                                                onChange={handleInputChange}
                                                className="w-full px-10 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500"
                                                placeholder="+91 9876543210"
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Section B: Association Type */}
                            <div className="space-y-4">
                                <h3 className="text-xl font-bold text-white">
                                     I wish to associate with the Bharat Grand Startup Challenge (BGSC) as
                                </h3>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {[
                                        'Startup / Founder',
                                        'Bank / Financial Institution',
                                        'Mentor / Domain Expert',
                                        'Academic / Incubator',
                                        'Corporate / Industry Partner',
                                        'Investor / Angel / VC',
                                        'Government / PSU Representative',
                                        'Volunteer / Ecosystem Partner'
                                    ].map((option) => (
                                        <label
                                            key={option}
                                            className={`flex items-center p-3 rounded-lg border cursor-pointer transition-all ${
                                                formData.associationType === option
                                                    ? 'bg-yellow-500/10 border-yellow-500 text-yellow-400'
                                                    : 'bg-gray-800 border-gray-700 hover:bg-gray-700 hover:border-gray-600'
                                            }`}
                                        >
                                            <input
                                                type="radio"
                                                name="associationType"
                                                value={option}
                                                checked={formData.associationType === option}
                                                onChange={handleInputChange}
                                                className="sr-only"
                                            />
                                            <div className={`w-5 h-5 rounded-full border mr-3 flex items-center justify-center ${
                                                formData.associationType === option
                                                    ? 'border-yellow-500'
                                                    : 'border-gray-600'
                                            }`}>
                                                {formData.associationType === option && (
                                                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                                                )}
                                            </div>
                                            <span>{option}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Section C: Area of Interest */}
                            <div className="space-y-4">
                                <h3 className="text-xl font-bold text-white">
                                     Area of Interest
                                </h3>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {[
                                        'Funding & Financial Support',
                                        'Pilot / PoC Opportunities',
                                        'CSR / ESG / Impact Initiatives',
                                        'Mentorship & Knowledge Sharing',
                                        'Market Access & Scaling',
                                        'Policy & Ecosystem Development'
                                    ].map((interest) => (
                                        <label
                                            key={interest}
                                            className={`flex items-center p-3 rounded-lg border cursor-pointer transition-all ${
                                                formData.interests.includes(interest)
                                                    ? 'bg-yellow-500/10 border-yellow-500 text-yellow-400'
                                                    : 'bg-gray-800 border-gray-700 hover:bg-gray-700 hover:border-gray-600'
                                            }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={formData.interests.includes(interest)}
                                                onChange={() => handleInterestChange(interest)}
                                                className="sr-only"
                                            />
                                            <div className={`w-5 h-5 rounded border mr-3 flex items-center justify-center ${
                                                formData.interests.includes(interest)
                                                    ? 'bg-yellow-500 border-yellow-500'
                                                    : 'bg-gray-800 border-gray-600'
                                            }`}>
                                                {formData.interests.includes(interest) && (
                                                    <svg className="w-3 h-3 text-gray-900" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                )}
                                            </div>
                                            <span>{interest}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Contribution Textarea */}
                            <div className="space-y-4">
                                <h3 className="text-xl font-bold text-white">
                                    How would you like to contribute to Bharat's growth through BGSC?
                                </h3>
                                
                                <textarea
                                    name="contribution"
                                    value={formData.contribution}
                                    onChange={handleInputChange}
                                    rows="4"
                                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500"
                                    placeholder="Share your thoughts on how you'd like to contribute..."
                                />
                            </div>

                            {/* Confirmation Checkboxes */}
                            <div className="space-y-4 p-4 bg-gray-800/50 rounded-lg">
                                <label className="flex items-start gap-3">
                                    <input
                                        type="checkbox"
                                        name="confirmInterest"
                                        checked={formData.confirmInterest}
                                        onChange={handleInputChange}
                                        className="mt-1 w-5 h-5 rounded border-gray-600 bg-gray-800 text-yellow-500 focus:ring-yellow-500 focus:ring-offset-gray-900"
                                        required
                                    />
                                    <span className="text-gray-300">
                                        I confirm my interest to be part of the Bharat Grand Startup Challenge (BGSC)
                                    </span>
                                </label>
                                
                                <label className="flex items-start gap-3">
                                    <input
                                        type="checkbox"
                                        name="consentContact"
                                        checked={formData.consentContact}
                                        onChange={handleInputChange}
                                        className="mt-1 w-5 h-5 rounded border-gray-600 bg-gray-800 text-yellow-500 focus:ring-yellow-500 focus:ring-offset-gray-900"
                                    />
                                    <span className="text-gray-300">
                                        I consent to be contacted regarding BGSC updates and initiatives
                                    </span>
                                </label>
                            </div>

                            {/* 🔒 reCAPTCHA Section */}
                            <div className="p-4 bg-gray-800/30 rounded-lg border border-gray-700">
                                <div className="flex items-center gap-2 mb-3">
                                    <FaRobot className="text-yellow-500" />
                                    <h4 className="text-lg font-bold text-white">Security Verification</h4>
                                </div>
                                
                                <div className="flex flex-col items-center justify-center">
                                    {/* reCAPTCHA Error Message */}
                                    {recaptchaScriptError && (
                                        <div className="mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                                            <p className="text-red-400 text-sm mb-2">
                                                <FaExclamationTriangle className="inline mr-2" />
                                                reCAPTCHA failed to load. This might be due to:
                                            </p>
                                            <ul className="text-gray-300 text-xs list-disc list-inside">
                                                <li>Ad blocker or privacy extension</li>
                                                <li>Network restrictions</li>
                                                <li>Browser settings</li>
                                            </ul>
                                            <button
                                                type="button"
                                                onClick={() => window.location.reload()}
                                                className="mt-3 px-4 py-2 bg-red-500 text-white rounded text-sm hover:bg-red-600"
                                            >
                                                Refresh Page
                                            </button>
                                        </div>
                                    )}
                                    
                                    {/* Show loading or reCAPTCHA */}
                                    {!recaptchaLoaded && !recaptchaScriptError ? (
                                        <div className="py-8">
                                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500 mx-auto mb-4"></div>
                                            <p className="text-gray-400 text-sm">Loading security verification...</p>
                                        </div>
                                    ) : recaptchaLoaded && RECAPTCHA_SITE_KEY ? (
                                        <ReCAPTCHA
                                            ref={recaptchaRef}
                                            sitekey={RECAPTCHA_SITE_KEY}
                                            onChange={handleRecaptchaChange}
                                            onExpired={handleRecaptchaExpire}
                                            theme="dark"
                                            size="normal"
                                        />
                                    ) : null}
                                    
                                    {recaptchaError && (
                                        <p className="text-red-400 text-sm mt-2 flex items-center gap-1">
                                            <FaExclamationTriangle className="text-xs" />
                                            Please complete the reCAPTCHA verification
                                        </p>
                                    )}
                                    
                                    <p className="text-gray-400 text-xs mt-3 text-center">
                                        This verification helps us prevent spam submissions and ensures security.
                                    </p>
                                </div>
                            </div>

                            {/* Submission Status */}
                            {submitStatus && (
                                <div className={`p-4 rounded-lg ${
                                    submitStatus.type === 'success' 
                                        ? 'bg-green-500/10 border border-green-500/30 text-green-400'
                                        : 'bg-red-500/10 border border-red-500/30 text-red-400'
                                }`}>
                                    {submitStatus.message}
                                </div>
                            )}

                            {/* Submit Button */}
                            <div className="flex gap-4 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowEnquiryPopup(false)}
                                    className="flex-1 px-6 py-3 bg-gray-800 border border-gray-700 text-gray-300 font-semibold rounded-lg hover:bg-gray-700 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting || !recaptchaToken}
                                    className="flex-1 px-6 py-3 bg-gradient-to-r from-yellow-500 to-yellow-600 text-gray-900 font-bold rounded-lg hover:from-yellow-400 hover:to-yellow-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-gray-900"></div>
                                            Submitting...
                                        </>
                                    ) : (
                                        'Submit Enquiry'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* 📘 ABOUT SECTIONS CONTAINER */}
            <div className="max-w-6xl mx-auto px-4 md:px-6 py-12 grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                {/* About BSGC Section */}
                <div className="bg-gray-900 rounded-2xl border-3 border-yellow-500 p-6 md:p-8 shadow-2xl hover:shadow-yellow-500/20 transition-shadow">
                    <div className="flex items-center gap-3 mb-6">
                        <h2 className="text-2xl md:text-3xl font-bold text-yellow-400">
                            {pageData.aboutSections?.bsgc?.title || "About BSGC 2026"}
                        </h2>
                    </div>

                    <div className="space-y-4">
                        {pageData.aboutSections?.bsgc?.description?.length > 0 ? (
                            pageData.aboutSections.bsgc.description.map((para, index) => (
                                <p
                                    key={index}
                                    className="text-gray-300 leading-relaxed"
                                    dangerouslySetInnerHTML={{
                                        __html: para || "Description not available"
                                    }}
                                />
                            ))
                        ) : (
                            <div className="text-gray-400 italic">
                                <p className="mb-2">The <strong>Bharat Startup Grand Challenge 2026</strong> is a DPIIT-approved national initiative to identify, support, and scale high-impact startup innovations across key sectors of the Indian economy.</p>
                                <p className="mb-2">The program brings together startups, industry leaders, government bodies, and investors to solve sector-specific challenges through innovation.</p>
                                <p>Selected startups gain mentorship, national visibility, ecosystem partnerships, and opportunities for pilots and funding support.</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* About MASA Forum Section */}
                <div className="bg-gray-900 rounded-2xl border-3 border-yellow-500 p-6 md:p-8 shadow-2xl hover:shadow-yellow-500/20 transition-shadow">
                    <div className="flex items-center gap-3 mb-6">
                        <h2 className="text-2xl md:text-3xl font-bold text-yellow-400">
                            {pageData.aboutSections?.masa?.title || "About MASA Forum"}
                        </h2>
                    </div>

                    <div className="space-y-4">
                        {pageData.aboutSections?.masa?.description?.length > 0 ? (
                            pageData.aboutSections.masa.description.map((para, index) => (
                                <p
                                    key={index}
                                    className="text-gray-300 leading-relaxed"
                                >
                                    {para || "Description not available"}
                                </p>
                            ))
                        ) : (
                            <div className="text-gray-400 italic">
                                <p className="mb-2">MASA Forum is a DPIIT-registered platform dedicated to accelerating the growth of MSMEs and startups across Bharat.</p>
                                <p>It connects entrepreneurs with government, industry, investors, and institutions to enable scale, governance, and long-term impact.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* 🔥 LIVE CHALLENGES SECTION */}
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-12">
                <div className="bg-gray-900 rounded-2xl border-3 border-yellow-500 p-6 md:p-8 shadow-2xl">
                    <div className="text-center mb-10">
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
                            {pageData.challenges?.title || "Live Challenges"}
                        </h2>
                        <p className="text-gray-300 max-w-2xl mx-auto text-lg">
                            {pageData.challenges?.subtitle || "Click on any challenge to view details and apply. Choose the challenge most relevant to your startup."}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {hasChallenges ? (
                            pageData.challenges.items.map((challenge, index) => {
                                const challengeKey = challenge.number || index;
                                const imageUrl = challengeImages[challengeKey];
                                const hasImageInData = challenge.image?.id;
                                const cacheKey = `challenge_${challengeKey}_${challenge.image?.id || 'no-id'}`;
                                const imageFailed = imageLoadErrors.has(cacheKey);

                                return (
                                    <a
                                        key={challengeKey}
                                        href={challenge.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="group bg-gray-800 hover:bg-gray-700 rounded-xl border border-gray-700 hover:border-yellow-500/50 p-5 transition-all duration-300 hover:shadow-xl hover:shadow-yellow-500/10 flex flex-col h-full"
                                    >
                                        {/* Challenge Image Section */}
                                        <div className="mb-4 rounded-lg overflow-hidden bg-gradient-to-br from-gray-900 to-gray-800 h-48 flex items-center justify-center relative">
                                            {imageUrl && !imageFailed ? (
                                                <>
                                                    <img
                                                        src={imageUrl}
                                                        alt={challenge.name || `Challenge ${index + 1}`}
                                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                                        onError={(e) => {
                                                            e.target.style.display = "none";
                                                            setImageLoadErrors(prev => {
                                                                const newSet = new Set(prev);
                                                                newSet.add(cacheKey);
                                                                return newSet;
                                                            });
                                                        }}
                                                    />
                                                    {challenge.image?.id && imageFailed && (
                                                        <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center p-4">
                                                            <FaExclamationTriangle className="text-yellow-500 text-2xl mb-2" />
                                                            <p className="text-sm text-gray-300 text-center">Image failed to load</p>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    retryImageLoad(challenge.image.id, false, challengeKey);
                                                                }}
                                                                className="mt-2 px-3 py-1 bg-yellow-500 text-gray-900 rounded text-xs hover:bg-yellow-600"
                                                            >
                                                                Retry
                                                            </button>
                                                        </div>
                                                    )}
                                                </>
                                            ) : hasImageInData && !imageFailed ? (
                                                <div className="flex flex-col items-center justify-center text-gray-400 p-4 w-full h-full">
                                                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500 mb-2"></div>
                                                    <span className="text-sm">Loading image...</span>
                                                </div>
                                            ) : (
                                                <div className="flex flex-col items-center justify-center text-gray-400 p-4 w-full h-full">
                                                    <FaImage className="text-5xl mb-2" />
                                                    <span className="text-sm">No image available</span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center gap-2">
                                                <div className="w-10 h-10 rounded-lg bg-yellow-500/20 border border-yellow-500/30 flex items-center justify-center">
                                                    <span className="text-lg font-bold text-yellow-400">
                                                        {challenge.number || index + 1}
                                                    </span>
                                                </div>
                                                <FaTrophy className="text-yellow-500" />
                                            </div>
                                            <div className="w-8 h-8 rounded-full bg-gray-700 group-hover:bg-yellow-500 flex items-center justify-center transition-colors">
                                                <FaExternalLinkAlt className="text-sm text-gray-400 group-hover:text-white" />
                                            </div>
                                        </div>

                                        <h3 className="text-lg font-semibold text-white group-hover:text-yellow-400 transition-colors mb-2 line-clamp-2 flex-grow">
                                            {challenge.name || `Challenge ${index + 1}`}
                                        </h3>

                                        <div className="mt-auto pt-3 border-t border-gray-700">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs text-gray-400 truncate" title={challenge.link}>
                                                    {challenge.link ? challenge.link.replace(/^https?:\/\//, '') : 'No link available'}
                                                </span>
                                                <FaArrowRight className="text-xs text-gray-500 group-hover:text-yellow-400 transition-colors" />
                                            </div>
                                        </div>
                                    </a>
                                );
                            })
                        ) : (
                            <div className="col-span-1 md:col-span-2 lg:col-span-3 text-center py-12">
                                <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <FaTrophy className="w-8 h-8 text-gray-400" />
                                </div>
                                <h3 className="text-xl font-semibold text-gray-300 mb-2">No Challenges Available</h3>
                                <p className="text-gray-400">Please check back later for upcoming challenges.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <Footer />
            
            {/* Tailwind CSS Animations */}
            <style jsx>{`
                @keyframes bounce {
                    0%, 100% {
                        transform: translateY(0);
                    }
                    50% {
                        transform: translateY(-10px);
                    }
                }
                @keyframes fadeIn {
                    from {
                        opacity: 0;
                    }
                    to {
                        opacity: 1;
                    }
                }
                @keyframes scaleIn {
                    from {
                        transform: scale(0.9);
                        opacity: 0;
                    }
                    to {
                        transform: scale(1);
                        opacity: 1;
                    }
                }
                .animate-bounce {
                    animation: bounce 2s infinite;
                }
                .animate-fade-in {
                    animation: fadeIn 0.5s ease-out;
                }
                .animate-scale-in {
                    animation: scaleIn 0.3s ease-out;
                }
            `}</style>
        </div>
    );
}

export default BharatStartupGrandChallengePage;